import { CheckboxEditor, DateTimeEditor, DropdownEditor, Field } from '@approvalmax/ui';
import { Grid } from '@approvalmax/ui/src/components';
import { useAtomValue } from 'jotai';
import { domain } from 'modules/data';
import { useSelector } from 'modules/react-redux';
import { SectionContainer } from 'pages/requestForm/components/SectionContainer';
import { getNetSuiteContext } from 'pages/requestForm/selectors/netsuite/netSuiteSelectors';
import { memo, useCallback, useMemo } from 'react';
import { useNetSuiteInvoiceFieldSettings } from 'shared/hooks';

import {
    NetSuiteEmployeesDataProvider,
    NetSuiteLeadSourcesDataProvider,
    NetSuiteOpportunitiesDataProvider,
    NetSuitePartnersDataProvider,
} from '../../../components';
import {
    isFieldEditable,
    isFieldMandatory,
    isFieldReadOnly,
    isFieldVisible,
    isOptionalFieldInvalid,
} from '../../../NetSuiteRequestForm/NetSuiteRequestForm.helpers';
import { netSuiteShowErrorsState } from '../../../resources/states';
import { useNetSuiteInvoiceFields } from '../../NetSuiteInvoiceRequestForm.hooks';
import { netSuiteInvoiceFieldValuesState } from '../../NetSuiteInvoiceRequestForm.states';
import { NetSuiteInvoiceFields } from '../../NetSuiteInvoiceRequestForm.types';
import { messages } from './SalesDetailsSection.messages';

export const SalesDetailsSection = memo(() => {
    const netSuiteEditingContext = useSelector(getNetSuiteContext);
    const showErrors = useAtomValue(netSuiteShowErrorsState);
    const fieldValues = useAtomValue(netSuiteInvoiceFieldValuesState);

    const { documentFields } = netSuiteEditingContext;

    const { onFieldChange } = useNetSuiteInvoiceFields();

    const fieldSettings = useNetSuiteInvoiceFieldSettings(documentFields);

    const isSalesDetailsSectionVisible = useMemo(
        () =>
            isFieldVisible(fieldSettings.salesRep) ||
            isFieldVisible(fieldSettings.opportunity) ||
            isFieldVisible(fieldSettings.salesEffectiveDate) ||
            isFieldVisible(fieldSettings.excludeCommissions) ||
            isFieldVisible(fieldSettings.partner) ||
            isFieldVisible(fieldSettings.leadSource),
        [fieldSettings]
    );

    const onEditorChange = useCallback(
        <K extends keyof NetSuiteInvoiceFields>(key: K) =>
            (value: NetSuiteInvoiceFields[K]) =>
                onFieldChange(key, value),
        [onFieldChange]
    );

    if (!isSalesDetailsSectionVisible) {
        return null;
    }

    return (
        <SectionContainer title={messages.salesDetailsSectionTitle}>
            <Grid gridTemplateColumns='repeat(auto-fill, 267px)' rowGap={24} columnGap={32}>
                {isFieldVisible(fieldSettings.salesRep) && (
                    <Field title={messages.salesRepFieldText} required={isFieldMandatory(fieldSettings.salesRep)}>
                        <NetSuiteEmployeesDataProvider integrationCode={domain.IntegrationCode.NetSuiteInvoice}>
                            <DropdownEditor
                                value={fieldValues.salesRep || null}
                                singleLinePerItem={false}
                                onChange={onEditorChange('salesRep')}
                                placeholder={
                                    isFieldEditable(fieldSettings.salesRep) ? messages.salesRepFieldPlaceholder : ''
                                }
                                invalid={isOptionalFieldInvalid(
                                    showErrors,
                                    fieldValues.salesRep,
                                    fieldSettings.salesRep
                                )}
                                disabled={isFieldReadOnly(fieldSettings.salesRep)}
                            />
                        </NetSuiteEmployeesDataProvider>
                    </Field>
                )}

                {isFieldVisible(fieldSettings.opportunity) && (
                    <Field title={messages.opportunityFieldText} required={isFieldMandatory(fieldSettings.opportunity)}>
                        <NetSuiteOpportunitiesDataProvider
                            integrationCode={domain.IntegrationCode.NetSuiteInvoice}
                            customerId={fieldValues.customer?.id}
                            subsidiaryId={fieldValues.subsidiary?.id}
                        >
                            <DropdownEditor
                                value={fieldValues.opportunity || null}
                                singleLinePerItem={false}
                                onChange={onEditorChange('opportunity')}
                                placeholder={
                                    isFieldEditable(fieldSettings.opportunity)
                                        ? messages.opportunityFieldPlaceholder
                                        : ''
                                }
                                invalid={isOptionalFieldInvalid(
                                    showErrors,
                                    fieldValues.opportunity,
                                    fieldSettings.opportunity
                                )}
                                disabled={isFieldReadOnly(fieldSettings.opportunity)}
                            />
                        </NetSuiteOpportunitiesDataProvider>
                    </Field>
                )}

                {isFieldVisible(fieldSettings.salesEffectiveDate) && (
                    <Field
                        title={messages.salesEffectiveDateFieldText}
                        required={isFieldMandatory(fieldSettings.salesEffectiveDate)}
                    >
                        <DateTimeEditor
                            value={fieldValues.salesEffectiveDate}
                            placeholder={
                                isFieldEditable(fieldSettings.salesEffectiveDate) ? messages.dateFieldPlaceholder : ''
                            }
                            disabled={isFieldReadOnly(fieldSettings.salesEffectiveDate)}
                            invalid={isOptionalFieldInvalid(
                                showErrors,
                                fieldValues.salesEffectiveDate,
                                fieldSettings.salesEffectiveDate
                            )}
                            onChange={onEditorChange('salesEffectiveDate')}
                        />
                    </Field>
                )}

                {isFieldVisible(fieldSettings.excludeCommissions) && (
                    <Field
                        title={messages.excludeCommissionsFieldText}
                        required={isFieldMandatory(fieldSettings.excludeCommissions)}
                    >
                        <CheckboxEditor
                            onChange={onEditorChange('excludeCommissions')}
                            value={fieldValues.excludeCommissions || false}
                            disabled={isFieldReadOnly(fieldSettings.excludeCommissions)}
                        >
                            {messages.checkboxFieldPositiveValue}
                        </CheckboxEditor>
                    </Field>
                )}

                {isFieldVisible(fieldSettings.partner) && (
                    <Field title={messages.partnerFieldText} required={isFieldMandatory(fieldSettings.partner)}>
                        <NetSuitePartnersDataProvider integrationCode={domain.IntegrationCode.NetSuiteInvoice}>
                            <DropdownEditor
                                value={fieldValues.partner || null}
                                singleLinePerItem={false}
                                onChange={onEditorChange('partner')}
                                placeholder={
                                    isFieldEditable(fieldSettings.partner) ? messages.partnerFieldPlaceholder : ''
                                }
                                invalid={isOptionalFieldInvalid(showErrors, fieldValues.partner, fieldSettings.partner)}
                                disabled={isFieldReadOnly(fieldSettings.partner)}
                            />
                        </NetSuitePartnersDataProvider>
                    </Field>
                )}

                {isFieldVisible(fieldSettings.leadSource) && (
                    <Field title={messages.leadSourceFieldText} required={isFieldMandatory(fieldSettings.leadSource)}>
                        <NetSuiteLeadSourcesDataProvider integrationCode={domain.IntegrationCode.NetSuiteInvoice}>
                            <DropdownEditor
                                value={fieldValues.leadSource || null}
                                singleLinePerItem={false}
                                onChange={onEditorChange('leadSource')}
                                placeholder={
                                    isFieldEditable(fieldSettings.leadSource) ? messages.leadSourceFieldPlaceholder : ''
                                }
                                invalid={isOptionalFieldInvalid(
                                    showErrors,
                                    fieldValues.leadSource,
                                    fieldSettings.leadSource
                                )}
                                disabled={isFieldReadOnly(fieldSettings.leadSource)}
                            />
                        </NetSuiteLeadSourcesDataProvider>
                    </Field>
                )}
            </Grid>
        </SectionContainer>
    );
});

SalesDetailsSection.displayName = 'SalesDetailsSection';
